import { useState, useEffect } from 'react';
import { X, Car, Cpu, Plus, Eye, Edit2, Trash2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui';
import { sgmDefaultTemplate } from '@/data/templates';
import { VehicleMilestoneTemplateModal } from './VehicleMilestoneTemplateModal';
import { ComponentTimingTemplateViewModal } from './ComponentTimingTemplateViewModal';
import { ComponentTimingTemplateEditModal } from './ComponentTimingTemplateEditModal';

interface TemplateShopModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TemplateType = 'vehicle' | 'component' | null;

interface VehicleMilestoneTemplate {
  id: string;
  oem: string;
  description: string;
  milestones: MilestoneItem[];
  created_at: string;
  updated_at: string;
  isDefault?: boolean;
}

interface MilestoneItem {
  id: string;
  sequence: number;
  name: string;
  description: string;
  required: boolean;
  functionalNode: '首次造车' | '可销售车' | 'N/A';
}

interface ComponentTimingTemplate {
  id: string;
  oem: string;
  product: string;
  description: string;
  phases: typeof sgmDefaultTemplate;
  created_at: string;
  updated_at: string;
}

const defaultVehicleMilestones: MilestoneItem[] = [
  { id: '1', sequence: 1, name: 'VPI', description: 'Vehicle Program Initiation', required: true, functionalNode: '项目立项' },
  { id: '2', sequence: 2, name: 'SF', description: 'Styling Freeze', required: false, functionalNode: 'N/A' },
  { id: '3', sequence: 3, name: 'VDR', description: 'Verified Data Release', required: true, functionalNode: '数据发布' },
  { id: '4', sequence: 4, name: 'MC1', description: 'Matching 1', required: false, functionalNode: 'N/A' },
  { id: '5', sequence: 5, name: 'APPV', description: 'Advanced Process Validation Vehicle', required: true, functionalNode: '首次造车' },
  { id: '6', sequence: 6, name: 'MC2', description: 'Matching 2', required: false, functionalNode: 'N/A' },
  { id: '7', sequence: 7, name: 'PPV', description: 'Process Validation Vehicle', required: false, functionalNode: 'N/A' },
  { id: '8', sequence: 8, name: 'VTC', description: 'Vehicle Testing Complete', required: false, functionalNode: 'N/A' },
  { id: '9', sequence: 9, name: 'VDC', description: 'Validation Documentation Complete', required: false, functionalNode: 'N/A' },
  { id: '10', sequence: 10, name: 'S', description: 'Salable', required: true, functionalNode: '可销售车' },
  { id: '11', sequence: 11, name: 'SORP', description: 'Start Of Regular Production', required: true, functionalNode: 'N/A' },
];

const defaultSGMVehicleTemplate: VehicleMilestoneTemplate = {
  id: 'default-sgm',
  oem: 'SGM',
  description: 'SGM 默认整车里程碑模板',
  milestones: defaultVehicleMilestones,
  created_at: '',
  updated_at: '',
  isDefault: true
};

export function TemplateShopModal({ isOpen, onClose }: TemplateShopModalProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedType, setSelectedType] = useState<TemplateType>(null);
  const [vehicleTemplates, setVehicleTemplates] = useState<VehicleMilestoneTemplate[]>([]);
  const [componentTemplates, setComponentTemplates] = useState<ComponentTimingTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateVehicleModal, setShowCreateVehicleModal] = useState(false);
  const [showViewComponentModal, setShowViewComponentModal] = useState(false);
  const [showEditComponentModal, setShowEditComponentModal] = useState(false);
  const [selectedComponentTemplate, setSelectedComponentTemplate] = useState<ComponentTimingTemplate | null>(null);
  const [editingVehicleTemplate, setEditingVehicleTemplate] = useState<VehicleMilestoneTemplate | null>(null);
  const [editingComponentTemplate, setEditingComponentTemplate] = useState<ComponentTimingTemplate | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  const getAccessToken = (): string | null => {
    const storedSession = localStorage.getItem('session');
    if (!storedSession) return null;
    try {
      const session = JSON.parse(storedSession);
      return session?.access_token || null;
    } catch {
      return null;
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const token = getAccessToken();
      const response = await fetch('/api/templates', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVehicleTemplates(data.vehicleTemplates || []);
        setComponentTemplates(data.componentTemplates || []);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeSelect = (type: TemplateType) => {
    setSelectedType(type);
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setSelectedType(null);
  };

  const handleDeleteVehicleTemplate = async (templateId: string) => {
    if (!confirm('确定要删除此模板吗？')) return;
    
    try {
      const token = getAccessToken();
      const response = await fetch(`/api/templates?id=${templateId}&type=vehicle`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        fetchTemplates();
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  const handleDeleteComponentTemplate = async (templateId: string) => {
    if (!confirm('确定要删除此模板吗？')) return;
    
    try {
      const token = getAccessToken();
      const response = await fetch(`/api/templates?id=${templateId}&type=component`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        fetchTemplates();
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  const handleViewComponentTemplate = (template: ComponentTimingTemplate) => {
    setSelectedComponentTemplate(template);
    setShowViewComponentModal(true);
  };

  const handleEditVehicleTemplate = (template: VehicleMilestoneTemplate) => {
    setEditingVehicleTemplate(template);
    setShowCreateVehicleModal(true);
  };

  const handleCreateVehicleTemplate = () => {
    setEditingVehicleTemplate(null);
    setShowCreateVehicleModal(true);
  };

  const handleVehicleTemplateSaved = () => {
    setShowCreateVehicleModal(false);
    setEditingVehicleTemplate(null);
    fetchTemplates();
  };

  const handleCreateComponentTemplate = () => {
    const newTemplate: ComponentTimingTemplate = {
      id: '',
      oem: 'SGM',
      product: 'Exterior Lighting',
      description: '新零部件时间计划模板',
      phases: JSON.parse(JSON.stringify(sgmDefaultTemplate)),
      created_at: '',
      updated_at: ''
    };
    setEditingComponentTemplate(newTemplate);
    setShowEditComponentModal(true);
  };

  const handleEditComponentTemplate = (template: ComponentTimingTemplate) => {
    setEditingComponentTemplate(template);
    setShowEditComponentModal(true);
  };

  const handleComponentTemplateSaved = () => {
    setShowEditComponentModal(false);
    setEditingComponentTemplate(null);
    fetchTemplates();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center">
            {step === 2 && (
              <button
                onClick={handleBack}
                className="mr-3 p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </button>
            )}
            <h3 className="text-lg font-bold text-gray-800">Template Shop</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <div className="space-y-6">
              <p className="text-gray-600 text-center mb-8">Please select a template type</p>
              
              <div className="grid grid-cols-2 gap-6">
                <button
                  onClick={() => handleTypeSelect('vehicle')}
                  className="p-6 border-2 border-gray-200 rounded-xl transition-all group hover:border-black hover:outline-2 hover:outline-black hover:outline-offset-[-2px]"
                >
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4">
                      <Car className="w-8 h-8 text-gray-600" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">Vehicle Milestone Template</h4>
                    <p className="text-sm text-gray-500 text-center">
                      Configure vehicle development milestones
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => handleTypeSelect('component')}
                  className="p-6 border-2 border-gray-200 rounded-xl transition-all group hover:border-black hover:outline-2 hover:outline-black hover:outline-offset-[-2px]"
                >
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4">
                      <Cpu className="w-8 h-8 text-gray-600" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">Component Timing Template</h4>
                    <p className="text-sm text-gray-500 text-center">
                      Configure component development tasks
                    </p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {step === 2 && selectedType === 'vehicle' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-md font-semibold text-gray-800">Vehicle Milestone Templates</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={handleCreateVehicleTemplate}
                >
                </Button>
              </div>

              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : (
                <div className="space-y-3">
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center">
                          <h5 className="font-semibold text-gray-800">{defaultSGMVehicleTemplate.oem}</h5>
                          <span className="ml-2 px-2 py-0.5 text-xs bg-gray-600 text-white rounded">Default</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{defaultSGMVehicleTemplate.description}</p>
                        <p className="text-xs text-gray-400 mt-2">
                          {defaultSGMVehicleTemplate.milestones.length} nodes
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditVehicleTemplate(defaultSGMVehicleTemplate)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {vehicleTemplates.length > 0 && (
                    <>
                      {vehicleTemplates.map((template) => (
                        <div
                          key={template.id}
                          className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <h5 className="font-semibold text-gray-800">{template.oem}</h5>
                              <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                              <p className="text-xs text-gray-400 mt-2">
                                {template.milestones.length} nodes
                              </p>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEditVehicleTemplate(template)}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteVehicleTemplate(template.id)}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 2 && selectedType === 'component' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-md font-semibold text-gray-800">Component Timing Templates</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={handleCreateComponentTemplate}
                >
                </Button>
              </div>

              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : (
                <div className="space-y-3">
                  {componentTemplates.length === 0 ? (
                    <div className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div>
                          <h5 className="font-semibold text-gray-800">SGM Exterior Lighting</h5>
                          <p className="text-sm text-gray-500 mt-1">SGM default template for lighting projects</p>
                          <p className="text-xs text-gray-400 mt-2">
                            {sgmDefaultTemplate.length} phases
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewComponentTemplate({
                              id: 'default',
                              oem: 'SGM',
                              product: 'Exterior Lighting',
                              description: 'SGM default template for lighting projects',
                              phases: sgmDefaultTemplate,
                              created_at: '',
                              updated_at: ''
                            })}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center">
                              <h5 className="font-semibold text-gray-800">SGM Exterior Lighting</h5>
                              <span className="ml-2 px-2 py-0.5 text-xs bg-gray-600 text-white rounded">Default</span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">SGM default template for lighting projects</p>
                            <p className="text-xs text-gray-400 mt-2">
                              {sgmDefaultTemplate.length} phases
                            </p>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleViewComponentTemplate({
                                id: 'default',
                                oem: 'SGM',
                                product: 'Exterior Lighting',
                                description: 'SGM default template for lighting projects',
                                phases: sgmDefaultTemplate,
                                created_at: '',
                                updated_at: ''
                              })}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              title="View"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                      {componentTemplates.map((template) => (
                        <div
                          key={template.id}
                          className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <h5 className="font-semibold text-gray-800">{template.oem} - {template.product}</h5>
                              <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                              <p className="text-xs text-gray-400 mt-2">
                                {template.phases.length} phases
                              </p>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEditComponentTemplate(template)}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleViewComponentTemplate(template)}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteComponentTemplate(template.id)}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end p-4 border-t bg-gray-50">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      <VehicleMilestoneTemplateModal
        isOpen={showCreateVehicleModal}
        onClose={() => {
          setShowCreateVehicleModal(false);
          setEditingVehicleTemplate(null);
        }}
        onSave={handleVehicleTemplateSaved}
        editingTemplate={editingVehicleTemplate}
      />

      <ComponentTimingTemplateViewModal
        isOpen={showViewComponentModal}
        onClose={() => {
          setShowViewComponentModal(false);
          setSelectedComponentTemplate(null);
        }}
        template={selectedComponentTemplate}
      />

      <ComponentTimingTemplateEditModal
        isOpen={showEditComponentModal}
        onClose={() => {
          setShowEditComponentModal(false);
          setEditingComponentTemplate(null);
        }}
        onSave={handleComponentTemplateSaved}
        editingTemplate={editingComponentTemplate}
      />
    </div>
  );
}
